import {
  $isListItemNode,
  $isListNode,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import { $convertFromMarkdownString, Transformer } from "@lexical/markdown";
import {
  $getRoot,
  $isLineBreakNode,
  $isParagraphNode,
  $isTextNode,
  ElementNode,
} from "lexical";
import {
  normalizeLegacyDataKeyMarkup,
  replaceLegacyDataKeyMarkup,
} from "./legacyDataKeyCompatibility";

const DATA_KEY_REFERENCE_REG_EXP = /{{\s*([a-z0-9]+(?:-[a-z0-9]+)+)\s*}}/g;

/**
 * Matches a single Handlebars block-control tag — `{{#if X}}`, `{{#unless X}}`,
 * `{{#each X}}`, `{{/if}}`, `{{/unless}}`, `{{/each}}`, or `{{else}}`. Plain
 * variable interpolations like `{{ foo }}` are NOT matched.
 */
export const HANDLEBARS_BLOCK_TAG_REG_EXP =
  /\{\{\s*(?:#if|#unless|#each|\/if|\/unless|\/each|else\b)\b[^{}]*?\}\}/;

const HANDLEBARS_BLOCK_TAG_LINE_REG_EXP = new RegExp(
  `^\\s*${HANDLEBARS_BLOCK_TAG_REG_EXP.source}\\s*$`
);

/** True when `line` is exactly a single Handlebars block-control tag. */
export const isHandlebarsBlockLine = (line: string): boolean =>
  HANDLEBARS_BLOCK_TAG_LINE_REG_EXP.test(line);

/**
 * Preprocesses markdown by converting HTML tags to markdown syntax. We have some
 * templates that contain HTML tags that we need to convert to markdown syntax.
 */
export const SEPARATOR_ROW_REG_EXP = /^\s*\|(\s*:?-{3,}:?\s*\|)+\s*$/;

export const convertHtmlTagsIntoMarkdownSyntaxOnly = (
  markdown: string
): string => {
  // Helper functions
  const countColumns = (row: string): number => {
    const trimmed = row.trim();
    if (!trimmed.startsWith("|")) {
      return 0;
    }
    // Handle rows with or without trailing |
    const parts = trimmed.split("|");
    // First element is empty (before first |), last element may be empty (after trailing |)
    // or content (if no trailing |)
    const hasTrailingPipe = trimmed.endsWith("|");
    return hasTrailingPipe ? parts.length - 2 : parts.length - 1;
  };

  const isTableRow = (line: string): boolean => {
    const trimmed = line.trim();
    // Accept rows that start with | - trailing | is optional
    // Must have at least 2 pipe characters (one at start, one separator)
    return trimmed.startsWith("|") && (trimmed.match(/\|/g) || []).length >= 2;
  };

  const isSeparatorRow = (line: string): boolean => {
    return SEPARATOR_ROW_REG_EXP.test(line);
  };

  const isValidTable = (table: string[]): boolean => {
    return table.length >= 2 || table.some((l) => isSeparatorRow(l));
  };

  // HTML conversion helpers
  const convertImageTag = (match: string): string => {
    const srcMatch = match.match(/src=["']([^"']+)["']/i);
    const altMatch = match.match(/alt=["']([^"']*)["']/i);
    const widthMatch = match.match(/width=["'](\d+)(?:px)?["']/i);
    const src = srcMatch ? srcMatch[1] : "";
    const alt = altMatch ? altMatch[1] : "";
    const width = widthMatch ? Number.parseInt(widthMatch[1], 10) : undefined;

    if (width && width > 0) {
      return `![${alt}](${src}){width:${width}}`;
    }

    return `![${alt}](${src})`;
  };

  const decodeHtmlEntities = (text: string): string => {
    return text
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, " ");
  };

  const isOnlyEmptyCellMarkup = (content: string): boolean => {
    const withoutEmptyParagraphs = content.replace(
      /<p\b[^>]*>(?:\s*<br\s*\/?\s*>\s*)?<\/p>/gi,
      ""
    );
    const withoutBreaks = withoutEmptyParagraphs.replace(/<br\s*\/?>/gi, "");
    const withoutEscapedNewlines = withoutBreaks.replace(/\\n/g, "");
    const withoutEntities = withoutEscapedNewlines.replace(/&nbsp;/gi, "");
    return withoutEntities.trim() === "";
  };

  // Convert HTML tags to markdown (for non-table content)
  const convertHtmlToMarkdown = (text: string): string => {
    let processed = text;

    // Images
    processed = processed.replaceAll(/<img\b[^>]*>/gi, convertImageTag);

    // Bold/strong
    processed = processed.replaceAll(/<b\b[^>]*>(.*?)<\/b>/gi, "**$1**");
    processed = processed.replaceAll(
      /<strong\b[^>]*>(.*?)<\/strong>/gi,
      "**$1**"
    );

    // Italic/emphasis
    processed = processed.replaceAll(/<i\b[^>]*>(.*?)<\/i>/gi, "*$1*");
    processed = processed.replaceAll(/<em\b[^>]*>(.*?)<\/em>/gi, "*$1*");

    // Code
    processed = processed.replaceAll(/<code\b[^>]*>(.*?)<\/code>/gi, "`$1`");

    // Links
    processed = processed.replaceAll(
      /<a\b[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi,
      "[$2]($1)"
    );

    // Empty paragraphs - convert to marker BEFORE handling br and regular paragraphs
    // This preserves empty paragraphs explicitly marked with <p><br /></p> or <p></p>
    // Must run before <br> conversion, otherwise <p><br /></p> becomes <p>\n</p>
    processed = processed.replaceAll(
      /<p\b[^>]*>(?:\s*<br\s*\/?\s*>\s*)?<\/p>/gi,
      EMPTY_PARAGRAPH_MARKER
    );

    // Line breaks - convert to CommonMark hard break syntax (two spaces + newline)
    // This ensures Lexical creates a LineBreakNode during import
    // The trailing spaces are cleaned up by cleanupTrailingSpacesAfterImport
    processed = processed.replaceAll(/<br\s*\/?>/gi, "  \n");

    // Paragraphs (non-empty)
    processed = processed.replaceAll(/<p\b[^>]*>(.*?)<\/p>/gi, "$1\n\n");

    // Headings
    processed = processed.replaceAll(/<h1\b[^>]*>(.*?)<\/h1>/gi, "# $1");
    processed = processed.replaceAll(/<h2\b[^>]*>(.*?)<\/h2>/gi, "## $1");
    processed = processed.replaceAll(/<h3\b[^>]*>(.*?)<\/h3>/gi, "### $1");
    processed = processed.replaceAll(/<h4\b[^>]*>(.*?)<\/h4>/gi, "#### $1");
    processed = processed.replaceAll(/<h5\b[^>]*>(.*?)<\/h5>/gi, "##### $1");
    processed = processed.replaceAll(/<h6\b[^>]*>(.*?)<\/h6>/gi, "###### $1");

    // Lists
    processed = processed.replaceAll(/<ul\b[^>]*>/gi, "\n");
    processed = processed.replaceAll(/<\/ul>/gi, "\n");
    processed = processed.replaceAll(/<ol\b[^>]*>/gi, "\n");
    processed = processed.replaceAll(/<\/ol>/gi, "\n");
    processed = processed.replaceAll(/<li\b[^>]*>(.*?)<\/li>/gi, "- $1\n");

    // Blockquotes
    processed = processed.replaceAll(
      /<blockquote\b[^>]*>(.*?)<\/blockquote>/gi,
      "> $1"
    );

    // Remove remaining HTML tags (but preserve <u> and <data-key> tags)
    processed = processed.replaceAll(/<(?!\/?u\b|\/?data-key\b)[^>]+>/gi, "");

    // Decode HTML entities
    processed = decodeHtmlEntities(processed);

    return processed;
  };

  // Process HTML in table cells
  // IMPORTANT: Preserve <br /> tags - they represent line breaks in table
  // cells, handled by markdownTransformers. Literal \\n sequences pass
  // through untouched (they are plain text; the legacy \\n-as-paragraph-break
  // convention was migrated to <br /> in the DB).
  const processCellHtml = (cellContent: string): string => {
    if (isOnlyEmptyCellMarkup(cellContent)) {
      return "";
    }

    let processed = cellContent;

    // Remove actual newlines (escaped \\n stays untouched - it is plain text)
    processed = processed.replace(/\r?\n/g, " ");

    // Convert images
    processed = processed.replaceAll(/<img\b[^>]*>/gi, convertImageTag);

    // Convert HTML tags to markdown
    processed = processed.replaceAll(/<b\b[^>]*>(.*?)<\/b>/gi, "**$1**");
    processed = processed.replaceAll(
      /<strong\b[^>]*>(.*?)<\/strong>/gi,
      "**$1**"
    );
    processed = processed.replaceAll(/<i\b[^>]*>(.*?)<\/i>/gi, "*$1*");
    processed = processed.replaceAll(/<em\b[^>]*>(.*?)<\/em>/gi, "*$1*");
    processed = processed.replaceAll(/<code\b[^>]*>(.*?)<\/code>/gi, "`$1`");
    processed = processed.replaceAll(
      /<a\b[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi,
      "[$2]($1)"
    );

    // Handle divs - extract content and normalize
    processed = processed.replaceAll(
      /<div[^>]*>([\s\S]*?)<\/div>/gi,
      (_match, content) => {
        // Only remove actual newlines
        return content.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
      }
    );

    // IMPORTANT: Preserve <br /> tags - they represent line breaks in table cells
    // The markdownTransformers will handle converting them to LineBreakNodes

    // Remove remaining HTML tags EXCEPT <br />, <u>, and <data-key> tags
    processed = processed.replaceAll(
      /<(?!br\s*\/?|\/?u\b|\/?data-key\b)[^>]+>/gi,
      ""
    );

    // Decode HTML entities
    processed = decodeHtmlEntities(processed);

    // Normalize multiple spaces to single space (but preserve content structure)
    processed = processed.replace(/  +/g, " ").trim();

    return processed;
  };

  // Step 1: Normalize multi-line table rows
  const lines = markdown.split(/\r?\n/);
  const normalizedLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // If line starts with | but doesn't end with |, it might be a multi-line row
    // OR it might be a table row without a trailing pipe (which is valid markdown)
    if (
      trimmed.startsWith("|") &&
      !trimmed.endsWith("|") &&
      !isSeparatorRow(line)
    ) {
      let rowContent = line;
      let j = i + 1;
      let foundEnd = false;

      while (j < lines.length && !foundEnd) {
        const nextLine = lines[j];
        const nextTrimmed = nextLine.trim();

        // Check if the next line is a NEW table row (starts with |)
        // If so, the current row is complete (just without a trailing pipe)
        if (nextTrimmed.startsWith("|") || isSeparatorRow(nextLine)) {
          // Current line is a complete table row without trailing pipe
          normalizedLines.push(line);
          foundEnd = true;
        } else if (nextTrimmed.endsWith("|")) {
          // Found the end of a multi-line row (doesn't start with | but ends with |)
          rowContent += " " + nextLine;
          const normalized = rowContent
            .replace(/\s+/g, " ")
            .replace(/\s*\|\s*/g, " | ")
            .trim();
          normalizedLines.push(normalized);
          i = j;
          foundEnd = true;
        } else if (
          (nextTrimmed === "" && j - i > 5) ||
          (!nextTrimmed.startsWith("|") && j - i > 10)
        ) {
          // Not a multi-line row - too many lines or blank line encountered
          normalizedLines.push(line);
          foundEnd = true;
        } else {
          rowContent += " " + nextLine;
          j++;
        }
      }

      if (!foundEnd) {
        normalizedLines.push(line);
      }
    } else {
      normalizedLines.push(line);
    }
  }

  // Step 2: Identify and extract tables
  const tables: string[] = [];
  const processedLines: string[] = [];
  let currentTable: string[] | null = null;
  let tableColumnCount: number | null = null;

  const endCurrentTable = () => {
    if (currentTable && isValidTable(currentTable)) {
      tables.push(currentTable.join("\n"));
      processedLines.push(`__TABLE_PLACEHOLDER_${tables.length - 1}__`);
    } else if (currentTable) {
      processedLines.push(...currentTable);
    }
    currentTable = null;
    tableColumnCount = null;
  };

  for (const line of normalizedLines) {
    if (isSeparatorRow(line)) {
      if (currentTable) {
        currentTable.push(line);
      } else {
        currentTable = [line];
        tableColumnCount = countColumns(line);
      }
      continue;
    }

    if (isTableRow(line)) {
      const columnCount = countColumns(line);

      if (currentTable) {
        if (tableColumnCount === null || columnCount === tableColumnCount) {
          currentTable.push(line);
          if (tableColumnCount === null) {
            tableColumnCount = columnCount;
          }
        } else {
          // Column count mismatch - end current table, start new one
          endCurrentTable();
          currentTable = [line];
          tableColumnCount = columnCount;
        }
      } else {
        currentTable = [line];
        tableColumnCount = columnCount;
      }
      continue;
    }

    // Not a table row
    endCurrentTable();
    processedLines.push(line);
  }

  endCurrentTable(); // Handle table at end of document

  // Step 3: Process non-table content
  let processed = processedLines.join("\n");
  processed = convertHtmlToMarkdown(processed);

  // Step 4: Restore and process tables
  processed = processed.replace(
    /__TABLE_PLACEHOLDER_(\d+)__/g,
    (match, index) => {
      const tableIndex = parseInt(index, 10);
      if (tableIndex >= tables.length) {
        return match;
      }

      const table = tables[tableIndex];
      const tableLines = table.split(/\r?\n/);
      let expectedColumnCount: number | null = null;

      const processedTableLines = tableLines.map((line) => {
        // Process separator rows
        if (isSeparatorRow(line)) {
          const columnCount = countColumns(line);
          const cellParts = line.split("|").slice(1, -1);
          const alignments = cellParts.map((part) => {
            const trimmed = part.trim();
            if (trimmed.startsWith(":") && trimmed.endsWith(":")) {
              return ":---:";
            } else if (trimmed.startsWith(":")) {
              return ":---";
            } else if (trimmed.endsWith(":")) {
              return "---:";
            }
            return "---";
          });

          if (expectedColumnCount === null) {
            expectedColumnCount = columnCount;
          }

          // Normalize separator row
          const separators = alignments.slice(0, columnCount);
          while (separators.length < columnCount) {
            separators.push("---");
          }
          return "| " + separators.join(" | ") + " |";
        }

        // Process data rows
        const columnCount = countColumns(line);
        if (columnCount === 0) {
          return line;
        }

        if (expectedColumnCount === null) {
          expectedColumnCount = columnCount;
        }

        const trimmedLine = line.trim();
        const hasTrailingPipe = trimmedLine.endsWith("|");
        const cellParts = trimmedLine.split("|");
        const cells: string[] = [];

        // Skip first element (empty, before first |) and last element if there's a trailing |
        const endIndex = hasTrailingPipe
          ? cellParts.length - 1
          : cellParts.length;
        for (let i = 1; i < endIndex; i++) {
          cells.push(processCellHtml(cellParts[i]));
        }

        // Ensure correct number of cells
        while (cells.length < expectedColumnCount) {
          cells.push("");
        }
        if (cells.length > expectedColumnCount) {
          cells.splice(expectedColumnCount);
        }

        return "| " + cells.join(" | ") + " |";
      });

      const processedTable = processedTableLines.join("\n");

      // Validate column counts
      const rowColumnCounts = processedTableLines
        .filter((l) => !isSeparatorRow(l))
        .map(countColumns);

      if (
        rowColumnCounts.length > 0 &&
        !rowColumnCounts.every((count) => count === rowColumnCounts[0])
      ) {
        return table; // Return original if invalid
      }

      return processedTable;
    }
  );

  return processed;
};

/**
 * The HTML representation of an empty paragraph in markdown.
 * We use <p><br /></p> because:
 * - It's valid HTML that CommonMark passes through unchanged
 * - The <br /> ensures consistent rendering (empty <p></p> may collapse)
 * - Renders reliably in browsers, PDFs, and other export formats
 * - Explicitly represents an empty paragraph (clear semantics)
 */
export const EMPTY_PARAGRAPH_HTML = "<p><br /></p>";

/**
 * Zero-width space used as an internal marker for empty paragraphs during processing.
 * We use ZWSP because it's invisible - if cleanup fails, users don't see garbage text.
 * Used temporarily during import/export and cleaned up immediately.
 */
export const EMPTY_PARAGRAPH_MARKER = "\u200B";

/**
 * Detects whether a line is plausibly a markdown table row.
 */
const isTableRowLine = (line: string): boolean => line.trim().startsWith("|");

const countTableRowColumns = (line: string): number => {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|")) return 0;
  // Strip leading and trailing pipes, then count cells split by unescaped `|`.
  let inner = trimmed.replace(/^\|/, "");
  if (inner.endsWith("|")) inner = inner.slice(0, -1);
  // Split on unescaped pipes.
  let depth = 0;
  let count = 1;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === "\\" && inner[i + 1] === "|") {
      i += 1;
      continue;
    }
    if (ch === "|" && depth === 0) count += 1;
  }
  return count;
};

/**
 * Wraps Handlebars block-tag lines (`{{#if X}}`, `{{/if}}`, `{{else}}`, etc.)
 * that sit adjacent to markdown table rows into ordinary table rows whose
 * first cell contains the raw tag and whose remaining cells are empty. This
 * prevents Lexical's table parser from terminating the table at the block
 * tag and inserting blank lines around it (which corrupts v1's section-8
 * conditional-row tables on round-trip).
 *
 * A block tag line is wrapped only when it sits within a contiguous region
 * that contains at least one real `|`-prefixed row, so that standalone
 * `{{#if}}`/`{{/if}}` blocks outside tables are left untouched.
 *
 * The wrapped row is padded to the column count of the surrounding table so
 * Lexical merges adjacent rows into one continuous table — its merge logic
 * requires column-count parity (see TABLE.replace in markdownTransformers).
 *
 * No sentinel prefix is used: the tag itself is the marker. On export,
 * TABLE.export detects rows whose first cell text is a bare Handlebars
 * block tag and emits the unwrapped `{{...}}` line.
 */
export const wrapHandlebarsTableBlocks = (markdown: string): string => {
  const lines = markdown.split(/\r?\n/);

  // First pass: classify each line and find regions of contiguous
  // table-or-handlebars-block lines.
  const classification: Array<"table" | "hbs-block" | "other"> = lines.map(
    (line) => {
      if (isTableRowLine(line)) return "table";
      if (isHandlebarsBlockLine(line)) return "hbs-block";
      return "other";
    }
  );

  // For each line, determine: should it be wrapped, and if so, what's the
  // column count of the surrounding table? Build a map index→colCount.
  const wrapColumnCount = new Array<number>(lines.length).fill(0);
  let regionStart = 0;
  while (regionStart < lines.length) {
    if (classification[regionStart] === "other") {
      regionStart += 1;
      continue;
    }
    let regionEnd = regionStart;
    while (regionEnd < lines.length && classification[regionEnd] !== "other") {
      regionEnd += 1;
    }
    let regionColCount = 0;
    for (let i = regionStart; i < regionEnd; i++) {
      if (classification[i] === "table") {
        const cols = countTableRowColumns(lines[i]);
        if (cols > regionColCount) regionColCount = cols;
      }
    }
    if (regionColCount > 0) {
      for (let i = regionStart; i < regionEnd; i++) {
        if (classification[i] === "hbs-block") {
          wrapColumnCount[i] = regionColCount;
        }
      }
    }
    regionStart = regionEnd;
  }

  return lines
    .map((line, i) => {
      const colCount = wrapColumnCount[i];
      if (colCount === 0) return line;
      const tag = line.trim();
      const cells = [` ${tag} `];
      for (let c = 1; c < colCount; c++) cells.push("   ");
      return `|${cells.join("|")}|`;
    })
    .join("\n");
};

/**
 * Converts empty paragraph HTML tags to ZWSP markers for import.
 * This allows us to recognize explicit empty paragraphs in markdown
 * and preserve them in the editor.
 *
 * Recognizes these formats:
 * - <p><br /></p> (preferred)
 * - <p><br/></p>
 * - <p><br></p>
 * - <p></p> (legacy/fallback)
 *
 * Example:
 * - "Text1\n\n<p><br /></p>\n\nText2" becomes "Text1\n\n\u200B\n\nText2"
 * - "Text1\n\n\n\nText2" stays as is (blank lines are ignored per CommonMark)
 */
export const convertEmptyParagraphHtmlToMarker = (markdown: string): string => {
  // Match <p><br /></p>, <p><br/></p>, <p><br></p>, or <p></p>
  return markdown.replace(
    /<p\s*>(?:\s*<br\s*\/?\s*>\s*)?<\/p>|<p\s*\/\s*>/gi,
    EMPTY_PARAGRAPH_MARKER
  );
};

/**
 * Converts ZWSP markers back to <p></p> HTML in exported markdown.
 * This is called after exporting markdown to restore the proper representation.
 *
 * Example:
 * - "Text1\n\n\u200B\n\nText2" becomes "Text1\n\n<p></p>\n\nText2"
 */
export const convertMarkersToEmptyParagraphHtml = (
  markdown: string
): string => {
  // Replace lines that contain only ZWSP (with optional surrounding whitespace)
  return markdown.replace(/^[ \t]*\u200B[ \t]*$/gm, EMPTY_PARAGRAPH_HTML);
};

/**
 * Post-processes exported markdown to ensure proper paragraph breaks.
 *
 * When using $convertToMarkdownString with shouldPreserveNewLines=true,
 * Lexical exports paragraphs separated by single newlines (\n).
 * Standard markdown requires double newlines (\n\n) for paragraph breaks.
 *
 * This function converts single newlines to double newlines for non-table content,
 * while preserving single newlines within table blocks (lines starting with |).
 *
 * It also preserves CommonMark hard line breaks: lines ending with two trailing
 * spaces ("  \n") indicate a <br> within a paragraph, not a paragraph break.
 *
 * Example:
 * - Input:  "a\n\u200B\na" (Lexical export with shouldPreserveNewLines=true)
 * - Output: "a\n\n\u200B\n\na" (proper markdown paragraph format)
 * - Tables: "| a |\n| --- |" stays as "| a |\n| --- |" (single newlines preserved)
 * - Hard breaks: "a  \nb" stays as "a  \nb" (two trailing spaces = hard break)
 */
export const normalizeExportedMarkdown = (markdown: string): string => {
  const lines = markdown.split("\n");
  const result: string[] = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prevLine = i > 0 ? lines[i - 1] : null;
    const isTableLine = line.trim().startsWith("|");
    const isBlankLine = line.trim() === "";
    // Check if previous line ends with two or more spaces (CommonMark hard break)
    const prevLineIsHardBreak = prevLine !== null && /  +$/.test(prevLine);
    // A Handlebars block line (e.g. `{{#if X}}`) appearing while we are
    // inside a table region was emitted by TABLE.export from a sentinel
    // row. Treat it as table-continuing so we do not insert blank lines
    // around it (which would split the markdown into separate tables).
    const isHbsBlockInTable = inTable && isHandlebarsBlockLine(line);

    if (isTableLine) {
      // Entering or continuing a table
      if (!inTable && result.length > 0 && result[result.length - 1] !== "") {
        // Add blank line before table (paragraph break before table)
        result.push("");
      }
      result.push(line);
      inTable = true;
    } else if (isHbsBlockInTable) {
      result.push(line);
    } else if (isBlankLine) {
      // Drop a blank line that sits between a Handlebars block-tag line
      // (e.g. `{{#if X}}...{{/if}}`) and an adjacent Handlebars block-tag
      // line OR list item that follows. Lexical's block-level serializer
      // inserts `\n\n` between paragraph and list blocks, but in v1's
      // shape these are written without a blank-line gap (the list
      // interrupts the paragraph in CommonMark). Blank lines before
      // standalone prose paragraphs are preserved so authored breaks
      // around `{{#if}}` blocks are kept.
      const prevResultLine = result[result.length - 1] ?? "";
      let nextNonBlankLine = "";
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim() !== "") {
          nextNonBlankLine = lines[j];
          break;
        }
      }
      const prevIsHbsBlock = HANDLEBARS_BLOCK_TAG_REG_EXP.test(prevResultLine);
      const nextIsHbsBlockOrList =
        HANDLEBARS_BLOCK_TAG_REG_EXP.test(nextNonBlankLine) ||
        /^\s*(?:[-*+]|\d+\.)\s+/.test(nextNonBlankLine);
      if (prevIsHbsBlock && nextIsHbsBlockOrList) {
        inTable = false;
        continue;
      }
      // Blank line - ends table if we were in one
      result.push(line);
      inTable = false;
    } else {
      // Non-table, non-blank line
      // When the current line or the previous result line carries a
      // Handlebars block-control tag (`{{#if}}`, `{{/if}}`, etc.), treat
      // them as continuing the same authored region — don't insert a
      // paragraph break between them. This preserves v1's shape where
      // consecutive `{{#if}}` lines and `{{#if}}` immediately followed by
      // a list/paragraph have no blank-line separator.
      const prevResultLine = result[result.length - 1] ?? "";
      const adjacentToHandlebarsBlock =
        HANDLEBARS_BLOCK_TAG_REG_EXP.test(line) ||
        HANDLEBARS_BLOCK_TAG_REG_EXP.test(prevResultLine);

      if (inTable) {
        // Exiting table to non-table content
        result.push(""); // Add blank line after table
      } else if (
        result.length > 0 &&
        result[result.length - 1] !== "" &&
        !adjacentToHandlebarsBlock
      ) {
        // Add blank line between paragraphs (not after blank lines)
        // BUT NOT if the previous line ends with hard break (two spaces)
        // Hard breaks are within-paragraph line breaks, not paragraph separators
        if (!prevLineIsHardBreak) {
          result.push("");
        }
      }
      result.push(line);
      inTable = false;
    }
  }

  return result.join("\n");
};

/**
 * Strips trailing spaces from TextNodes that precede LineBreakNodes.
 * This should be called AFTER importing markdown with CommonMark hard breaks.
 *
 * When Lexical imports "a  \nb" (CommonMark hard break), it creates:
 * - TextNode("a  ") with trailing spaces
 * - LineBreakNode
 * - TextNode("b")
 *
 * This function removes those trailing spaces so the user sees clean text.
 */
export const cleanupTrailingSpacesAfterImport = (
  root: ReturnType<typeof $getRoot>
): void => {
  const processElement = (element: ElementNode) => {
    const children = element.getChildren();
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const nextChild = children[i + 1];

      // If this is a TextNode followed by a LineBreakNode, strip trailing spaces
      if ($isTextNode(child) && nextChild && $isLineBreakNode(nextChild)) {
        const currentText = child.getTextContent();
        // Remove trailing spaces (CommonMark hard break syntax artifacts)
        const trimmedText = currentText.replace(/\s+$/, "");
        if (trimmedText !== currentText) {
          child.setTextContent(trimmedText);
        }
      }

      // Recursively process child elements
      if (child instanceof ElementNode && !$isParagraphNode(child)) {
        processElement(child);
      }
    }
  };

  // Process each element in the root
  for (const child of root.getChildren()) {
    if (child instanceof ElementNode) {
      processElement(child);
    }
  }
};

/**
 * Returns the list items (direct children) of a ListNode.
 */
const $getDirectListItems = (list: ListNode): ListItemNode[] =>
  list.getChildren().filter((c): c is ListItemNode => $isListItemNode(c));

/**
 * True when `item` is a "wrapper" ListItemNode — an item whose only children
 * are nested ListNodes (no inline text). Lexical's markdown importer emits
 * these wrappers at any level when a list contains nested content of a
 * different type, splitting the logical list and lifting the nested list
 * out at root with such a wrapper.
 */
const $isWrapperOnlyItem = (item: ListItemNode): boolean => {
  const children = item.getChildren();
  if (children.length === 0) return false;
  return children.every((c) => $isListNode(c));
};

/**
 * Peels through layers of wrapper-only items to reach the inner ListNode that
 * actually holds non-wrapper items (items with text). For example, given:
 *
 *   ol[start=1]
 *     li(indent=0)     ← wrapper
 *       ol[start=1]
 *         li(indent=1) ← wrapper
 *           ol[start=1]
 *             li(indent=2) "real text"
 *
 * peeling returns the innermost ol with the indent=2 li. This lets the repair
 * know the real depth of the misplaced content so it can attach to the right
 * spot in the previous list.
 */
const $peelWrappers = (list: ListNode): ListNode => {
  let current = list;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const items = $getDirectListItems(current);
    if (items.length !== 1) return current;
    const onlyItem = items[0];
    if (!$isWrapperOnlyItem(onlyItem)) return current;
    const innerLists = onlyItem
      .getChildren()
      .filter((c): c is ListNode => $isListNode(c));
    if (innerLists.length !== 1) return current;
    current = innerLists[0];
  }
};

/**
 * Walks `current`'s last-item chain until a ListItemNode at indent
 * `targetIndent` is reached. Used to find the attachment point for content
 * that the importer split off — e.g. content authored at 4-space indent
 * (indent=1) attaches under the indent=0 last item of the previous list.
 */
const $findItemAtIndent = (
  current: ListNode,
  targetIndent: number
): ListItemNode | null => {
  const items = $getDirectListItems(current);
  let candidate: ListItemNode | null = items[items.length - 1] ?? null;
  while (candidate && candidate.getIndent() < targetIndent) {
    const nestedList = candidate
      .getChildren()
      .find((c): c is ListNode => $isListNode(c));
    if (!nestedList) return null;
    const nestedItems = $getDirectListItems(nestedList);
    const last = nestedItems[nestedItems.length - 1] ?? null;
    if (!last) return null;
    candidate = last;
  }
  return candidate && candidate.getIndent() === targetIndent ? candidate : null;
};

/**
 * Grafts `incoming` (a freshly-peeled ListNode containing real items) into
 * the existing tree under `current`. If the right attachment point already
 * has a nested list of the same listType, the incoming items are appended to
 * that existing list rather than introduced as a duplicate sibling list.
 */
const $graftListIntoTree = (current: ListNode, incoming: ListNode): void => {
  const incomingItems = $getDirectListItems(incoming);
  if (incomingItems.length === 0) {
    incoming.remove();
    return;
  }
  const realIndent = incomingItems[0].getIndent();
  const target = $findItemAtIndent(current, realIndent - 1);
  if (!target) {
    // Could not find a matching anchor — leave the incoming list where it is.
    return;
  }
  const existingNested = target
    .getChildren()
    .find(
      (c): c is ListNode =>
        $isListNode(c) && c.getListType() === incoming.getListType()
    );
  if (existingNested) {
    for (const item of incoming.getChildren()) {
      existingNested.append(item);
    }
    incoming.remove();
  } else {
    target.append(incoming);
  }
};

/**
 * Re-merges sibling root-level ListNodes that Lexical's markdown importer
 * splits apart when a list contains nested content of a different type
 * (e.g. `1. First\n    - bullet\n    1. inner\n2. Second`).
 *
 * In that input the importer produces multiple sibling root-level lists: the
 * outer list, the lifted nested sub-list, and sometimes a tail list whose
 * first item is a wrapper for further nested content followed by real items
 * (like "Second") that belong to the outer list.
 *
 * This pass walks the root and, whenever the next sibling is a ListNode
 * whose first item is a wrapper, peels the wrapper(s) to find the real
 * nested list, grafts it into the previous list at the depth implied by its
 * items' indent, merges with an existing same-typed nested list when one is
 * present (avoiding duplicate sibling lists), and finally promotes any
 * remaining real items up to the previous list before removing the now-empty
 * intermediate list.
 */
const $repairMisplacedListNesting = (root: ElementNode): void => {
  let i = 0;
  while (i < root.getChildrenSize() - 1) {
    const current = root.getChildAtIndex(i);
    const next = root.getChildAtIndex(i + 1);
    if (!current || !next || !$isListNode(current) || !$isListNode(next)) {
      i++;
      continue;
    }

    const nextItems = $getDirectListItems(next);
    const firstNextItem = nextItems[0];
    if (!firstNextItem || !$isWrapperOnlyItem(firstNextItem)) {
      i++;
      continue;
    }

    for (const nested of firstNextItem.getChildren()) {
      if (!$isListNode(nested)) continue;
      const peeled = $peelWrappers(nested);
      $graftListIntoTree(current, peeled);
    }
    firstNextItem.remove();

    for (const item of next.getChildren()) {
      current.append(item);
    }
    next.remove();
    // Re-examine `current` against the new neighbor — there may be more
    // sibling lists to absorb (e.g. ol → ul → ol all at the root).
  }
};

/**
 * Options for importing markdown into the Lexical editor.
 */
export interface ImportMarkdownOptions {
  /** Whether to preserve empty paragraphs marked with <p></p> in the markdown */
  preserveEmptyParagraphs?: boolean;
  /** Whether to skip HTML tag conversion (use for pre-processed content like diff results) */
  skipHtmlConversion?: boolean;
  /** Custom post-processing function to run after import */
  postProcess?: (root: ReturnType<typeof $getRoot>) => void;
}

/**
 * Cleans up ZWSP markers from paragraphs after import, leaving them truly empty.
 * This is called after importing markdown that had <p></p> converted to ZWSP markers.
 */
export const cleanupEmptyParagraphMarkers = (
  root: ReturnType<typeof $getRoot>
): void => {
  for (const child of root.getChildren()) {
    if ($isParagraphNode(child)) {
      const textContent = child.getTextContent();
      // If paragraph contains only ZWSP marker(s), clear it to make it truly empty
      if (textContent.length > 0 && textContent.replace(/\u200B/g, "") === "") {
        child.clear();
      }
    }
  }
};

/**
 * Shared helper to import markdown into the Lexical editor with consistent
 * preprocessing and postprocessing. This ensures both normal and diff modes
 * handle markdown consistently.
 *
 * Steps:
 * 1. Convert <p></p> tags to internal markers (before HTML processing)
 * 2. Optionally convert HTML tags to markdown syntax
 * 3. Import markdown into Lexical
 * 4. Clean up trailing spaces from hard breaks
 * 5. Clean up empty paragraph markers to leave truly empty paragraphs
 * 6. Run any custom post-processing
 *
 * @param markdown - The raw markdown string to import
 * @param transformers - Lexical markdown transformers to use
 * @param options - Import options
 */
export const importMarkdownToLexical = (
  markdown: string,
  transformers: Transformer[],
  options: ImportMarkdownOptions = {}
): void => {
  const {
    preserveEmptyParagraphs: shouldPreserveEmpty = false,
    skipHtmlConversion = false,
    postProcess,
  } = options;

  let processedMarkdown = normalizeLegacyDataKeyMarkup(markdown);

  if (shouldPreserveEmpty) {
    processedMarkdown = convertEmptyParagraphHtmlToMarker(processedMarkdown);
  }

  if (!skipHtmlConversion) {
    processedMarkdown =
      convertHtmlTagsIntoMarkdownSyntaxOnly(processedMarkdown);
  }

  processedMarkdown = wrapHandlebarsTableBlocks(processedMarkdown);

  $convertFromMarkdownString(processedMarkdown, transformers);

  const root = $getRoot();
  $repairMisplacedListNesting(root);
  cleanupTrailingSpacesAfterImport(root);

  if (shouldPreserveEmpty) {
    cleanupEmptyParagraphMarkers(root);
  }

  if (postProcess) {
    postProcess(root);
  }
};

/**
 * Flattens data key references to their embedded snapshot values.
 * - {{ key }} => key
 * - legacy <data-key ...>value</data-key> => value
 * - @dk[key]{doc:"..."} => key
 */
export const flattenDataKeyReferencesToSnapshotValues = (
  markdown: string,
  resolveReference?: (dataKey: string, sourceDocId?: string) => string
): string => {
  const markdownWithResolvedLegacyReferences = replaceLegacyDataKeyMarkup(
    markdown,
    (dataKey, sourceDocId) =>
      resolveReference?.(dataKey, sourceDocId) ?? dataKey
  );
  const normalizedMarkdown = normalizeLegacyDataKeyMarkup(
    markdownWithResolvedLegacyReferences
  );
  return normalizedMarkdown.replace(
    DATA_KEY_REFERENCE_REG_EXP,
    (_match: string, dataKey: string) => resolveReference?.(dataKey) ?? dataKey
  );
};
